import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Gift, Clock, Mail } from 'lucide-react';
import axios from 'axios';

import { useLanguage } from '../context/LanguageContext';
import { VoucherCard } from '../components/voucher/VoucherCard';
import { VoucherCheckout } from '../components/voucher/VoucherCheckout';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const FALLBACK_PRODUCTS = [
  { id: 'family45', price_eur: 100 },
  { id: 'family75', price_eur: 150 },
  { id: 'newborn15', price_eur: 130 },
  { id: 'newborn25', price_eur: 180 },
  { id: 'maternity', price_eur: 100 },
  { id: 'maternityFamily', price_eur: 150 },
];

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
  viewport: { once: true },
};

export default function Vouchers() {
  const { t } = useLanguage();
  const [products, setProducts] = useState(FALLBACK_PRODUCTS);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/vouchers/products`);
        if (!cancelled && Array.isArray(data.products) && data.products.length) {
          setProducts(data.products);
        }
      } catch {
        // keep fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function handleBuy(product) {
    setSelected(product);
    setOpen(true);
  }

  return (
    <div data-testid="vouchers-page" className="pt-24 bg-warm-50">
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto">
            <span className="text-accent font-medium text-sm tracking-wider uppercase mb-3 block">
              {t.vouchers.subtitle}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold text-warm-900 mb-4">
              {t.vouchers.title}
            </h1>
            <p className="text-warm-600 text-lg leading-relaxed">{t.vouchers.intro}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-6 mt-10"
          >
            <Feature icon={Gift} label={t.vouchers.perfectGift} />
            <Feature icon={Clock} label={t.vouchers.validity} />
            <Feature icon={Mail} label={t.vouchers.instant} />
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((product) => (
              <VoucherCard
                key={product.id}
                product={product}
                onBuy={handleBuy}
              />
            ))}
          </div>
        </div>
      </section>

      <section id="voucher-terms" className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-2xl md:text-3xl text-warm-900 mb-6 text-center">
              {t.vouchers.terms.title}
            </h2>
            <ul className="space-y-3">
              {t.vouchers.terms.items.map((item, i) => (
                <li
                  key={i}
                  className="flex gap-3 text-warm-700 leading-relaxed"
                >
                  <span className="text-accent mt-1">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      <VoucherCheckout product={selected} open={open} onClose={() => setOpen(false)} />
    </div>
  );
}

function Feature({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-warm-600 text-sm">
      <Icon className="w-4 h-4 text-accent" />
      <span>{label}</span>
    </div>
  );
}
